import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Gift } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { promotionService } from '../services/api'
import { PageSpinner } from '../components/ui'
import { PromoPageCard } from '../components/PromoTemplates'

export default function Promotions() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['promotions'],
    queryFn: promotionService.getMyPromos,
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
  })

  const readMutation    = useMutation({ mutationFn: promotionService.markRead,  onSuccess: () => qc.invalidateQueries(['promotions']) })
  const dismissMutation = useMutation({ mutationFn: promotionService.dismiss,   onSuccess: () => qc.invalidateQueries(['promotions']) })

  const promos = (Array.isArray(data) ? data : []).filter(p => !p.dismissed)
  const unread = promos.filter(p => !p.read)

  useEffect(() => {
    if (!isAuthenticated || !promos.length) return
    promos.filter(p => !p.read).forEach(p => readMutation.mutate(p.id))
  }, [data]) // eslint-disable-line

  if (!isAuthenticated) return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center">
      <Gift size={48} className="text-gray-300 mx-auto mb-4"/>
      <h2 className="text-xl font-display font-bold text-gray-700 dark:text-gray-300 mb-2">Sign in to view promotions</h2>
      <p className="text-gray-500 text-sm">Exclusive offers are waiting for you</p>
    </div>
  )

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Gift size={24} className="text-primary"/> My Promotions
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {promos.length === 0
            ? 'No active promotions right now'
            : `${promos.length} offer${promos.length !== 1 ? 's' : ''} available${unread.length > 0 ? ` · ${unread.length} new` : ''}`}
        </p>
      </div>

      {isLoading ? (
        <PageSpinner/>
      ) : promos.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">🎁</div>
          <h3 className="font-display font-bold text-gray-600 dark:text-gray-400 mb-2">No promotions yet</h3>
          <p className="text-gray-400 text-sm">Check back soon — exclusive deals are on their way!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {promos.map((promo, i) => (
            <PromoPageCard
              key={promo.id}
              promo={promo}
              index={i}
              onDismiss={(id) => dismissMutation.mutate(id)}
              onCTA={(p) => { readMutation.mutate(p.id); if (p.cta_link) navigate(p.cta_link) }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
